/* src/core/env.ts — typed, validated access to build-time configuration.
 *
 * Everything here comes from `.env` via Vite's `VITE_` prefix. Nothing here is a secret:
 * the anon key is designed to be public and is only safe because RLS is on every table
 * (PLAN.md Part 3 §8). The service-role key is server-side only and never appears in this
 * file, this bundle, or any browser.
 */

function str(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed !== undefined && trimmed.length > 0 ? trimmed : null;
}

/*
 * Note the explicit null check. `Number(null)` is 0, and 0 is finite, so testing only
 * `Number.isFinite` meant an *unset* variable silently became 0 rather than the fallback. That
 * is invisible for as long as every variable this guards happens to be set in `.env` — which
 * every spawn coordinate is — and wrong the first time one is not.
 */
function num(value: string | undefined, fallback: number): number {
  const raw = str(value);
  if (raw === null) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const SUPABASE_URL = str(import.meta.env.VITE_SUPABASE_URL);
export const SUPABASE_ANON_KEY = str(import.meta.env.VITE_SUPABASE_ANON_KEY);

/** True only when both halves of the Supabase config are present. */
export const isSupabaseConfigured: boolean =
  SUPABASE_URL !== null && SUPABASE_ANON_KEY !== null;

/**
 * The API base. Same-origin `/api` matches the original's observed layout (PLAN.md Part 1 §3);
 * against a hosted Supabase project the Edge Functions live under `/functions/v1` instead.
 */
export const API_BASE_URL: string =
  str(import.meta.env.VITE_API_BASE_URL) ??
  (SUPABASE_URL !== null ? `${SUPABASE_URL}/functions/v1` : '/api');

/** World extent, `[west, south, east, north]`. Default is the Sydney working bbox. */
export const WORLD_BBOX: readonly [number, number, number, number] = (() => {
  const raw = str(import.meta.env.VITE_WORLD_BBOX);
  const fallback: [number, number, number, number] = [150.98, -34.02, 151.32, -33.75];
  if (raw === null) return fallback;

  const parts = raw.split(',').map((p) => Number(p.trim()));
  if (parts.length !== 4 || parts.some((p) => !Number.isFinite(p))) {
    console.warn(`[env] VITE_WORLD_BBOX is malformed ("${raw}"); using the default bbox`);
    return fallback;
  }
  return [parts[0] as number, parts[1] as number, parts[2] as number, parts[3] as number];
})();

export const SPAWN_LON: number = num(import.meta.env.VITE_SPAWN_LON, 151.104);
export const SPAWN_LAT: number = num(import.meta.env.VITE_SPAWN_LAT, -33.913);

export const IS_DEV: boolean = import.meta.env.DEV;

/**
 * Wipe the player's run on every load, so a refresh replays the opening from scratch.
 *
 * Temporary. There are no accounts yet, so an anonymous session silently resumes a half-played
 * game and the first minute — the part being built and rebalanced — becomes unreachable without
 * clearing site data by hand. `?keep` overrides it for one load; `VITE_GF_RESET_ON_LOAD=false`
 * turns it off for good. Delete both when accounts land.
 */
export const RESET_ON_LOAD: boolean = (() => {
  if (str(import.meta.env.VITE_GF_RESET_ON_LOAD) === 'false') return false;
  // Guarded because this module is also reachable from non-window contexts.
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has('keep')) return false;
  return true;
})();
